import Raven from 'raven-js';

import { urls } from '~/preloaded';
import settings from '~/settings';
import { CLAN_CREATION_STATUSES } from '~/api/const';
import { ATTRIBUTES_VALIDATION_STATE } from '~/constants';
import { get, post } from '~/helpers/api';
import { clanCost } from '~/helpers/clan';
import { ClanRec2IsEnabled, darwinIsEnabled } from '~/helpers/common';
import { delay } from '~/helpers/delay';
import { showRecommendationsClanSettingsDialog } from '~/helpers/dialogs';
import Loader from '~/helpers/loader';
import { t } from '~/helpers/localization';
import { trimAndRemoveSpaces } from '~/helpers/strings';
import { ROLE_NAMES } from '~/roles';
import { currentAccountActions } from '~/store/slices/currentAccountSlice';
import { syncAccountInfoThunk } from '~/store/thunks/accountThunks';
import { initClanWarsThunk } from '~/store/thunks/clanWarsThunks';
import { promiseWithGlobalSpinner } from '~/store/utils/globalSpinner';

import {
  sendClanCreationAlreadyInClanNotification,
  sendClanCreationErrorNotification,
  sendClanCreationNotEnoughGoldNotification,
  sendClanCreationNotification,
  sendClanCreationTagOrNameNotAvailableNotification,
} from '~/web2ClientAPI/notifications';
import { getClanTasksThunk } from '~/Actions/ActionClanTasks';

import type { IApiClanCreateCheckStatus, IApiCheckTagOrNameResponse, IApiError } from '../types/api';
import type { InferActionsType } from '~/Reducers';
import type { IClanCreateStateFields } from '~/Reducers/ReducerClanCreate';
import type { IAppDispatch, AppAsyncThunk } from '~/store';
import type { Nullable } from '~/types/generics';

const CLAN_CREATE_WAIT_DELAY = 500;

export const ERROR_CREATION_CLAN = 'ERROR_CREATION_CLAN';
export const FINISH_CHECK_AVAILABILITY_CLAN_CREATION = 'FINISH_CHECK_AVAILABILITY_CLAN_CREATION';
export const SET_INITIAL_STATE_CREATION_CLAN = 'SET_INITIAL_STATE_CREATION_CLAN';
export const START_CHECK_AVAILABILITY_CLAN_CREATION = 'START_CHECK_AVAILABILITY_CLAN_CREATION';
export const START_CREATION_CLAN = 'START_CREATION_CLAN';
export const UPDATE_CREATION_CLAN_FIELD_VALUE = 'UPDATE_CREATION_CLAN_FIELD_VALUE';

export type ActionsType = InferActionsType<typeof actionsClanCreate>;
export type ICreateErrorsCommon = Nullable<{
  errors: {
    [key in keyof IClanCreateStateFields]?: string[];
  };
}>;

export type FinishCreationType = {
  field: keyof IClanCreateStateFields;
  value?: string;
  available: ATTRIBUTES_VALIDATION_STATE;
  error: IApiError;
};

export const actionsClanCreate = {
  startCreationClan: () =>
    ({
      type: START_CREATION_CLAN,
    }) as const,

  errorCreationClan: (error: IApiError | ICreateErrorsCommon | IApiClanCreateCheckStatus) =>
    ({
      type: ERROR_CREATION_CLAN,
      error,
    }) as const,

  updateFieldValueCreationClan: (field: keyof IClanCreateStateFields, value: string, error: IApiError) =>
    ({
      type: UPDATE_CREATION_CLAN_FIELD_VALUE,
      field,
      value,
      error,
    }) as const,

  startCheckAvailabilityCreationClan: (field: keyof IClanCreateStateFields) =>
    ({
      type: START_CHECK_AVAILABILITY_CLAN_CREATION,
      field,
    }) as const,

  finishCheckAvailabilityCreationClan: (data: FinishCreationType) =>
    ({
      type: FINISH_CHECK_AVAILABILITY_CLAN_CREATION,
      data,
    }) as const,

  setInitialStateCreationClan: () =>
    ({
      type: SET_INITIAL_STATE_CREATION_CLAN,
    }) as const,
};

export const checkNameOrTagAvailability = async (
  field: keyof IClanCreateStateFields,
  value: string,
  signal?: AbortSignal,
): Promise<FinishCreationType> => {
  try {
    const url = urls.checkClanNameTagAvability;
    const payload = { [field]: value };

    const response = await post<IApiCheckTagOrNameResponse>(url, payload, signal && { signal });

    const data = response.current[field];
    let error: string | null = null;

    if (!data?.available) {
      const defaultError = {
        tag: t('Клан с таким тегом уже зарегистрирован.'),
        name: t('Клан с таким названием уже зарегистрирован'),
      };
      error = data?.reason || defaultError[field];
    }

    const available = data?.available
      ? ATTRIBUTES_VALIDATION_STATE.AVAILABLE
      : ATTRIBUTES_VALIDATION_STATE.NOT_AVAILABLE;

    return {
      field,
      available,
      error,
      value: trimAndRemoveSpaces(value),
    };
  } catch {
    return {
      field,
      value,
      available: ATTRIBUTES_VALIDATION_STATE.UNDEFINED,
      error: t('Не удалось проверить: техническая ошибка'),
    };
  }
};

const isPendingStatus = (status: CLAN_CREATION_STATUSES) => {
  return [CLAN_CREATION_STATUSES.CREATING, CLAN_CREATION_STATUSES.PAYMENT_REQUESTED].includes(status);
};

const hasDataStatus = (e: unknown): e is { data: { status?: CLAN_CREATION_STATUSES } } => {
  return (
    typeof e === 'object' &&
    e !== null &&
    'data' in e &&
    typeof (e as { data: unknown }).data === 'object' &&
    (e as { data: unknown }).data !== null &&
    'status' in (e as { data: { status?: CLAN_CREATION_STATUSES } }).data
  );
};

const checkCreationClanStatus = async (
  currentStatus?: CLAN_CREATION_STATUSES,
  forceCheck = false,
): Promise<{ status: CLAN_CREATION_STATUSES; clan?: IApiClanCreateCheckStatus['clan'] }> => {
  if (currentStatus && !isPendingStatus(currentStatus)) {
    return { status: currentStatus };
  }

  if (!forceCheck) {
    await delay(CLAN_CREATE_WAIT_DELAY);
  }

  try {
    const response = await get<IApiClanCreateCheckStatus>(urls.clanCheckStatus);

    if (response?.error) {
      throw new Error(response.error);
    }

    if (response?.clan?.status && response?.clan) {
      return { status: response.clan.status, clan: response.clan };
    }

    if (!response.status || isPendingStatus(response.status)) {
      return await checkCreationClanStatus(response.status || response?.clan?.status, forceCheck);
    }

    return { status: response.status, clan: response?.clan };
  } catch (error: unknown) {
    if (hasDataStatus(error)) {
      const status = error.data.status;
      if (status && (isPendingStatus(status) || status === null)) {
        return await checkCreationClanStatus(status, forceCheck);
      }
    }

    return { status: CLAN_CREATION_STATUSES.ERROR };
  }
};

const processCreationClanStatus = async (
  dispatch: IAppDispatch,
  json: IApiClanCreateCheckStatus,
  forceCheck?: boolean,
) => {
  const currentStatus = json?.status || json?.clan?.status;

  const { status, clan = json?.clan } = await checkCreationClanStatus(currentStatus, forceCheck);

  if (status === CLAN_CREATION_STATUSES.ACTIVE) {
    if (clan) {
      const cost = clanCost();
      sendClanCreationNotification(clan, cost);
    }

    dispatch(
      currentAccountActions.updateAccountInfo({
        clanId: clan?.id,
        roleName: ROLE_NAMES.COMMANDER,
        activeInvitesCount: 0,
      }),
    );
    dispatch(actionsClanCreate.setInitialStateCreationClan());
    dispatch(initClanWarsThunk(true));

    if (darwinIsEnabled) {
      void dispatch(getClanTasksThunk());
    }

    if (ClanRec2IsEnabled) {
      dispatch(showRecommendationsClanSettingsDialog());
    }

    return;
  }

  if (status === CLAN_CREATION_STATUSES.PAYMENT_REJECTED) {
    sendClanCreationNotEnoughGoldNotification();
    dispatch(actionsClanCreate.errorCreationClan(json));

    return;
  }

  if (status === CLAN_CREATION_STATUSES.ERROR) {
    sendClanCreationErrorNotification();
    dispatch(actionsClanCreate.errorCreationClan(json));

    return;
  }

  const msg = `Undefined behavior for clan status: ${status}`;
  console.error(msg);

  if (settings.sentryUrl) {
    Raven.captureMessage(msg, { extra: json });
  }

  dispatch(actionsClanCreate.errorCreationClan(null));
};

const forceSyncAccountInfo = async (
  dispatch: IAppDispatch,
  data: { additional_info?: { clan_id?: number; reason?: string } },
) => {
  try {
    await dispatch(syncAccountInfoThunk());
    dispatch(actionsClanCreate.setInitialStateCreationClan());
  } catch {
    const clanId = data?.additional_info?.clan_id || null;
    if (clanId) {
      dispatch(
        currentAccountActions.updateAccountInfo({
          clanId,
          roleName: ROLE_NAMES.PRIVATE,
          activeInvitesCount: 0,
        }),
      );
    }
    dispatch(actionsClanCreate.setInitialStateCreationClan());
  } finally {
    sendClanCreationAlreadyInClanNotification();
  }
};

export const creationClan =
  ({ signal }: { signal?: AbortSignal } = {}): AppAsyncThunk =>
  async (dispatch, getState) => {
    const state = getState();

    if (state.clanCreate.isProcessing) {
      const msg = 'Clan in creation process';
      console.error(msg);
      if (settings.sentryUrl) {
        Raven.captureMessage(msg, { extra: state.clanCreate });
      }
      return;
    }

    const { tag, name } = state.clanCreate;
    const availableState = ATTRIBUTES_VALIDATION_STATE.AVAILABLE;

    if (tag.available !== availableState || name.available !== availableState) {
      console.error('Tag or name is not available');
      return;
    }

    const loader = new Loader(t('Создаём клан'));
    loader.start(dispatch);

    dispatch(actionsClanCreate.startCreationClan());

    try {
      const url = urls.clanCreate;
      const payload = {
        tag: tag.value,
        name: name.value,
      };

      const response = await post<IApiClanCreateCheckStatus>(url, payload, signal && { signal });

      if (response.errors || response.additional_info || response.status === CLAN_CREATION_STATUSES.ERROR) {
        if (response.errors && response.description) {
          sendClanCreationErrorNotification(response.description);
          dispatch(actionsClanCreate.errorCreationClan(response));
          return;
        }

        if (response?.additional_info?.reason === 'account_already_in_clan') {
          return await forceSyncAccountInfo(dispatch, response);
        }

        if (response.errors && (response.errors.name || response.errors.tag)) {
          sendClanCreationTagOrNameNotAvailableNotification();
          dispatch(actionsClanCreate.errorCreationClan(response));

          return;
        }

        sendClanCreationErrorNotification();
        dispatch(actionsClanCreate.errorCreationClan(response));

        return;
      }

      if (response.status === CLAN_CREATION_STATUSES.REDIRECT) {
        dispatch(actionsClanCreate.errorCreationClan(response));

        return;
      }

      return await processCreationClanStatus(dispatch, response);
    } catch (error) {
      sendClanCreationErrorNotification();

      dispatch(
        actionsClanCreate.errorCreationClan({
          error: t('Произошла ошибка. Повторите попытку позже'),
        }),
      );

      return;
    } finally {
      loader.stop(dispatch);
    }
  };

export const waitForClanCreation = (): AppAsyncThunk => async (dispatch, getState) => {
  const state = getState();
  const status = state.currentAccount.clanStatus;

  if (status && [CLAN_CREATION_STATUSES.CREATING, CLAN_CREATION_STATUSES.PAYMENT_REQUESTED].includes(status)) {
    const promise = processCreationClanStatus(dispatch, { status }, true);

    return promiseWithGlobalSpinner(dispatch, promise, t('Создаём клан')).catch(() => {
      return sendClanCreationErrorNotification();
    });
  }
};
